import * as Sentry          from '@sentry/react';
import get                  from 'lodash/get';
import { Collection }       from '@mathquis/modelx';
import { ConnectorResults } from '@mathquis/modelx';
import { Model }            from '@mathquis/modelx';
import { observable }       from 'mobx';

export class ApiCollection<T extends Model = Model> extends Collection<T> {
  @observable
  public filters: Record<string, any> = {
      itemsPerPage: 1000, // max allowed items per page by the api
      // pagination: false, // removed because all queries should have pagination, this has no effect on the api side
    };

  @observable
  public sorts: Record<string, 'asc' | 'desc'> = {};

  @observable
  public total = 0;

  public findBy(property: string | Record<string, any>, value?: any): T | null | undefined {
    if (typeof property === 'string') {
      return this.find((model: Model) => model.get(property) === value);
    }

    const keys = Object.keys(property);

    return this.find(model => {
      return keys.reduce((previousValue, currentKey) => {
        if (model.get(currentKey) !== property[currentKey]) {
          return false;
        }

        return previousValue;
      }, true);
    });
  }

  public getUniques(property: string): string[] {
    const uniquesObj: Record<string, any> = {};
    this.forEach(model => {
      const val = model.get(property);
      if (val) {
        uniquesObj[val] = val;
      } else {
        console.warn(`Property ${property} not found`);
      }
    });
    return Object.keys(uniquesObj);
  }

  public onListSuccess(results: ConnectorResults, options: object) {
    const members = get(results, 'res.data[hydra:member]');

    // Successful response without a Hydra collection (HTML page served by a
    // proxy, empty body...) - report it to identify the endpoint instead of
    // crashing in modelx on `items.map`.
    if (!Array.isArray(members)) {
      Sentry.captureMessage('List response without hydra:member', {
        level: 'warning',
        extra: {
          request_url: results.res?.config?.url,
          request_method: results.res?.config?.method,
          status: results.res?.status,
          content_type: results.res?.headers?.['content-type'],
          data_type: typeof results.res?.data,
        },
      });
    }

    const connectorResults = new ConnectorResults(
      Array.isArray(members) ? members : [],
      results.res,
    );
    this.total = connectorResults.res.data?.['hydra:totalItems'] ?? 0;
    super.onListSuccess(connectorResults, options);
  }

  public prepareListOptions(options: any) {
    const sorts: Record<string, string> = {};
    Object.keys(this.sorts).forEach(key => {
      if (key) {
        sorts[`order[${key}]`] = this.sorts[key];
      }
    });

    const allFilters = {
      ...this.filters,
      ...sorts,
      ...options.filters,
    };

    return super.prepareListOptions({
      ...options,
      filters: allFilters,
      // sort: this.sorting,
    });
  }

  public setFilter(field: string, value: any) {
    this.filters[field] = value;

    return this;
  }

  public setFilters(filters: Record<string, any>) {
    Object.keys(filters).forEach(key => {
      this.setFilter(key, filters[key]);
    });

    return this;
  }

  public setSort(field: string, asc = true) {
    this.sorts[field] = asc ? 'asc' : 'desc';

    return this;
  }

  public setSorts(sorts: Record<string, boolean> = {}) {
    Object.keys(sorts).forEach(key => {
      this.setSort(key, sorts[key]);
    });

    return this;
  }
}
